/**
 * Analytics Utility
 * =================
 * Lightweight, non-blocking analytics tracking for frontend.
 * 
 * RULES:
 * - Must NEVER throw or break UX
 * - Fire-and-forget pattern
 * - Falls back to console.log in dev if backend unavailable
 */

const BACKEND_URL = window.location.origin;

/**
 * Track an analytics event.
 * 
 * @param {string} name - Event name (e.g., "vip_tier_selected")
 * @param {object} props - Optional event properties
 * @returns {void} - Fire-and-forget, no return value
 * 
 * @example
 * track("vip_tier_selected", { event_id: "abc123", tier: "vip" });
 */
export function track(name, props = {}) {
  try {
    const token = localStorage.getItem("token");
    
    // Log in dev
    if (process.env.NODE_ENV === "development") {
      console.log(`[Analytics] ${name}`, props);
    }
    
    // Fire-and-forget POST to backend
    fetch(`${BACKEND_URL}/api/analytics/track`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        event_name: name,
        props,
      }),
    }).catch((err) => {
      // Analytics must never break UX, but log in dev to aid debugging.
      if (process.env.NODE_ENV !== "production") console.error("[Analytics] track fetch failed:", err);
    });
    
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.error("[Analytics] track error:", err);
  }
}

/**
 * Track with explicit user context (for cases where localStorage isn't available)
 * 
 * @param {string} name - Event name
 * @param {object} props - Event properties
 * @param {string|null} token - Auth token (optional)
 */
export function trackWithToken(name, props = {}, token = null) {
  try {
    if (process.env.NODE_ENV === "development") {
      console.log(`[Analytics] ${name}`, props);
    }
    
    fetch(`${BACKEND_URL}/api/analytics/track`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        event_name: name,
        props,
      }),
    }).catch((err) => {
      if (process.env.NODE_ENV !== "production") console.error("[Analytics] trackWithToken fetch failed:", err);
    });
    
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.error("[Analytics] trackWithToken error:", err);
  }
}

export default { track, trackWithToken };
